/**
 * 全局 portal 入口分流 (web 管理后台) — REQ-20260514-0005
 *
 * 镜像 web-mall/src/auth/PortalEntryRouter.tsx。包在 App 外层。
 * 任意 URL 进入 web 时先做一次身份感知决策：
 *   - URL 含 `?force=admin` query → 跳过分流（buyer 想 demo admin 后台）
 *   - 调后端 portal-suggest 接口
 *     · target=mall + url 不属于当前 origin → window.location.href 跳走
 *     · 其它 (target=admin / stay / 同 origin) → 渲染 children
 *
 * 决策期间渲染 antd Spin。
 */
import React, { useEffect, useState, type ReactNode } from 'react';
import { Spin } from 'antd';
import { getPortalSuggestion } from '@/api/portalSuggest';
import { getToken } from '@/utils/storage';

interface Props {
  children: ReactNode;
}

const PUBLIC_AUTH_PATHS = new Set(['/login', '/auth/login', '/tenant/login', '/platform/login']);

const PortalEntryRouter: React.FC<Props> = ({ children }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // ?force=admin — 跳过分流（buyer 想看 admin 演示）
    if (params.get('force') === 'admin') {
      setReady(true);
      return;
    }
    // ?pb=1 — 从对端被踢过来的，不再二次跳转，防跨域死循环
    if (params.get('pb') === '1') {
      setReady(true);
      return;
    }
    if (PUBLIC_AUTH_PATHS.has(window.location.pathname)) {
      setReady(true);
      return;
    }
    // portal-suggest is an authenticated endpoint. On public entry/login pages,
    // skip it to avoid a background 401 being surfaced as a login exception.
    if (!getToken()) {
      setReady(true);
      return;
    }

    let cancelled = false;
    getPortalSuggestion()
      .then((decision) => {
        if (cancelled) return;

        // 仅当 backend 建议跳 mall 且目标 origin ≠ 当前 origin 时跳
        // 附加 ?pb=1 防止 mall 端再踢回来
        if (decision.target === 'mall' && decision.url) {
          try {
            const targetUrl = new URL(decision.url, window.location.origin);
            if (targetUrl.origin !== window.location.origin) {
              const dest = new URL(decision.url);
              dest.searchParams.set('pb', '1');
              window.location.href = dest.toString();
              return;
            }
          } catch {
            // url 解析失败 → 退化为渲染 children
          }
        }
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f0f2f5',
        }}
        data-testid="portal-entry-router-loading"
      >
        <Spin size="large" />
        <span style={{ marginLeft: 12, color: '#666' }}>正在进入...</span>
      </div>
    );
  }

  return <>{children}</>;
};

export default PortalEntryRouter;
